/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';
import session from '../../../../share/session';
import { REQUEST_STATUS_COMPLETED, REQUEST_STATUS_FAILED } from "../../../../share/util/api/polling";
import individualDiagnose from '../../../../share/util/individualDiagnose';
import error from '../../../../common/nodejs/errors';
import { apiErrorDefaultAction } from '../../../../share/util/api/errorAction';

/** 各フェーズ定義 */
const PHASE = {
  /** 待機 */
  STANDBY: 'standby',
  /** 実行監視 */
  WATCH: 'watch',
  /** 停止 */
  SUCCESS: 'success',
  /** 実行条件不成立表示 */
  NOT_HOLD_CONDITION: 'notHoldCondition',
  /** 開始不可表示 */
  NOT_START_FAILED: 'notStartFailed',
  /** VCPキャリブレーション失敗表示 */
  VCP_FAILED: 'vcpFailed',
  /** 共通エラー */
  FAILED: 'failed'
};

export default {
  name: 'requirement162',
  data() {
    return {
      phase: PHASE.STANDBY,
      isLoading: false,
      isDispose: true,
      systemId: '',
      modal_requirement_162: false,
      exitPromise: null
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),

    /** ダイアログのヘッダー色 */
    headerColor() {
      switch (this.phase) {
        case PHASE.NOT_HOLD_CONDITION:
        case PHASE.NOT_START_FAILED:
        case PHASE.VCP_FAILED:
        case PHASE.FAILED:
          return 'danger';
        case PHASE.SUCCESS:
          return 'success';
        default:
          return 'secondary';
      }
    },

    /** ダイアログのヘッダータイトル */
    headerTitle() {
      // 確認（待機）
      const confirmationTitle = 'message.common_title_confirmation';
      // 実行中（実行監視）
      const executionTitle = 'individual.common_title_execution';
      // 成功（停止）
      const successTitle = 'message.common_title_success';
      // エラー（実行条件不成立表示, 開始不可表示, VCPキャリブレーション失敗表示, 共通エラー）
      const errorTitle = 'message.common_title_error';

      const titleMap = {
        [PHASE.STANDBY]: confirmationTitle,
        [PHASE.WATCH]: executionTitle,
        [PHASE.SUCCESS]: successTitle,
        [PHASE.NOT_HOLD_CONDITION]: errorTitle,
        [PHASE.NOT_START_FAILED]: errorTitle,
        [PHASE.VCP_FAILED]: errorTitle,
        [PHASE.FAILED]: errorTitle,
      };
      return this.$t(titleMap[this.phase]);
    },

    /** ダイアログの表示文言 */
    workSupportMessage() {
      const messageMap = {
        [PHASE.STANDBY]: `${this.i18nWsKey}.REQ_WS_162_TXT_1`,
        [PHASE.WATCH]: `${this.i18nWsKey}.REQ_WS_162_TXT_2`,
        [PHASE.SUCCESS]: `${this.i18nWsKey}.REQ_WS_162_TXT_3`,
        [PHASE.NOT_HOLD_CONDITION]: `${this.i18nWsKey}.REQ_WS_162_TXT_4`,
        [PHASE.NOT_START_FAILED]: `${this.i18nWsKey}.REQ_WS_162_TXT_5`,
        [PHASE.VCP_FAILED]: `${this.i18nWsKey}.REQ_WS_162_TXT_6`,
        [PHASE.FAILED]: 'individual.error_communication_error',
      };
      return this.$t(messageMap[this.phase]);
    },

    /** OKボタンの表示有無 */
    showButtonOk() {
      return this.phase !== PHASE.WATCH;
    },

    /** キャンセルボタンの表示有無 */
    showButtonCancel() {
      return this.phase === PHASE.STANDBY;
    },
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化処理 */
    init() {
      this.modal_requirement_162 = true;
      this.systemId = individualDiagnose.getCurrentSystemId(this);
    },

    /** データ読出し(セッション遷移を伴う) */
    async readData() {
      try {
        this.isLoading = true;

        // バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'read'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (!response) {
          // pollingWspApiはエラーコールバックの終了を待たずnullを返す
          // 非同期処理を含んでいるshowUnexpectedErrorが呼ばれた場合、スピナー消えてしまうためPromiseの解決を待つ
          if (this.exitPromise) await this.exitPromise;
          return;
        }

        this.isDispose = response.data.user_data.is_dispose;
        switch (response.data.status) {
          // 正常終了
          case REQUEST_STATUS_COMPLETED:
            // ルーチン実行に遷移
            await this.execute();
            break;
          // 異常終了  
          case REQUEST_STATUS_FAILED:
            switch (response.data.async_code) {
              case error.WSP_162_EXECUTION_CONDITIONS_FAILED:
                // 実行条件不成立表示画面に遷移
                await this.dispose();
                this.phase = PHASE.NOT_HOLD_CONDITION;
                break;
              case error.WSP_162_CANNOT_START:
                // 開始不可表示画面に遷移
                await this.terminate();
                this.phase = PHASE.NOT_START_FAILED;
                break;
              case error.WSP_GENERAL_ERROR:
              default:
                // 共通エラーに遷移
                await this.showUnexpectedError();
                break;
            }
            break;
          default:
            // 共通エラーに遷移
            await this.showUnexpectedError();
            break;
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** ルーチン実行 */
    async execute() {
      try {
        this.isLoading = true;

        // バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'execute'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (!response) {
          // pollingWspApiはエラーコールバックの終了を待たずnullを返す
          // 非同期処理を含んでいるshowUnexpectedErrorが呼ばれた場合、スピナー消えてしまうためPromiseの解決を待つ
          if (this.exitPromise) await this.exitPromise;
          return;
        }

        switch (response.data.status) {
          // 正常終了
          case REQUEST_STATUS_COMPLETED:
            // 実行監視画面に遷移
            this.phase = PHASE.WATCH;
            // ルーチン実行監視を実行
            await this.monitor();
            break;
          // 異常終了
          case REQUEST_STATUS_FAILED:
            // VCPキャリブレーション失敗表示画面に遷移
            await this.terminate();
            this.phase = PHASE.VCP_FAILED;
            break;
          default:
            // 共通エラーに遷移
            await this.showUnexpectedError();
            break;
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** ルーチン結果確認 */
    async monitor() {
      try {
        this.isLoading = true;

        // バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'monitor'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (!response) {
          // pollingWspApiはエラーコールバックの終了を待たずnullを返す
          // 非同期処理を含んでいるshowUnexpectedErrorが呼ばれた場合、スピナー消えてしまうためPromiseの解決を待つ
          if (this.exitPromise) await this.exitPromise;
          return;
        }

        switch (response.data.status) {
          // 正常終了
          case REQUEST_STATUS_COMPLETED:
            // 停止画面に遷移
            await this.terminate();
            this.phase = PHASE.SUCCESS;
            break;
          // 異常終了
          case REQUEST_STATUS_FAILED:
            // VCPキャリブレーション失敗表示画面に遷移
            await this.terminate();
            this.phase = PHASE.VCP_FAILED;
            break;
          default:
            // 共通エラーに遷移
            await this.showUnexpectedError();
            break;
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** 終了 */
    async terminate() {
      try {
        this.isLoading = true;

        // バックエンドAPI POST,GET
        // ここではAPIの実行のみ
        const token = session.getToken();
        await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'terminate'
          },
          // エラー発生のコールバックを実装
          undefined,
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result, 'terminate'); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result, 'terminate'); },
        );
      } finally {
        this.isLoading = false;
      }
    },

    /** dispose */
    async dispose() {
      try {
        this.isLoading = true;

        // バックエンドAPI POST,GET
        // ここではAPIの実行のみ
        const token = session.getToken();
        await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'dispose'
          },
          // エラー発生のコールバックを実装
          undefined,
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result, 'dispose'); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result, 'dispose'); },
        );
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     * @param {object} calledFrom 呼び出し元
     */
    validateErrorResult(result,calledFrom = '') {
      apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => {
          result;
          // terminate,disposeから呼ばれた場合は共通エラーに遷移しない
          if (calledFrom === 'terminate' || calledFrom === 'dispose') return;
          this.showUnexpectedError();
        },
      );
    },

    /** 共通エラー */
    async showUnexpectedError() {
      // スピナー消えてしまうのを防ぐためPromiseを保持し、各API失敗時に解決を待つ
      this.exitPromise = this.isDispose ? this.dispose() : this.terminate();
      await this.exitPromise;
      this.phase = PHASE.FAILED;
    },

    /** OKボタン押下時の処理 */
    onOkClicked() {
      switch (this.phase) {
        // 待機
        case PHASE.STANDBY:
          this.readData();
          break;
        // 共通エラー  
        case PHASE.FAILED:
          this.closeWizard();
          break;
        // 停止  
        // 実行条件不成立表示
        // 開始不可表示
        // VCPキャリブレーション失敗表示
        case PHASE.SUCCESS:
        case PHASE.NOT_HOLD_CONDITION:
        case PHASE.NOT_START_FAILED:
        case PHASE.VCP_FAILED:
          this.closeWizard();
          break;
        default:
          break;
      }
    },

    /** キャンセルボタン押下時の処理 */
    onCancelClicked() {
      this.closeWizard();
    },

    /** Wizardを閉じる */
    closeWizard() {
      this.$emit('close-wizard');
      this.modal_requirement_162 = false;
    },
  },

  mounted() {
    this.init();
  },
};